import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession } from '../getSession';
import { getAllPermissionByModuleCodeUserId } from '../utils/lead';
import { handleLogout } from '../utils/logout';
import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

/**
 * Custom hook to retrieve and check module permissions.
 * Mirrors the legacy jQuery pattern:
 *   objUserAuth.GetModuleActionList($('[id$=hdnModuleCode]').val(), $('[id$=hdnSipUserId]').val(), objPermId)
 *
 * API response fields: Add, Edit, View, Delete, Search, Import, Export, MassUpdate, MassDelete
 *
 * @param {string} moduleCode - The module identifier (e.g. 'PAG10274')
 * @param {string} defaultMode - Mode for permission retrieval (default: 'S')
 */
export const useModulePermissions = (moduleCode, defaultMode = 'S') => {
    const navigate = useNavigate();
    const [permissions, setPermissions] = useState(null);
    const [loading, setLoading] = useState(true);
    const session = getSession();
    const userId = session?.userId || session?.UserId;
    const TokenId = session?.TokenId || session?.token;

    useEffect(() => {
        if (!moduleCode) {
            setLoading(false);
            return;
        }

        const fetchPermissions = async () => {
            if (!userId) {
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const raw = await getAllPermissionByModuleCodeUserId({
                    Mode: defaultMode,
                    ModuleCode: moduleCode,
                    UserId: userId,
                    Token: TokenId
                });

                // Normalize: API Details may return an array of rows or a single object
                let permData = raw;
                if (Array.isArray(raw)) {
                    // Take the first row — the user's permission record for this module
                    permData = raw.length > 0 ? raw[0] : null;
                } else if (typeof raw === 'string') {
                    try { permData = JSON.parse(raw); } catch (e) { permData = null; }
                }

                setPermissions(permData);
            } catch (err) {
                console.error(`[useModulePermissions] Permission fetch error for ${moduleCode}:`, err);
                setPermissions(null);
            } finally {
                setLoading(false);
            }
        };

        fetchPermissions();
    }, [moduleCode, defaultMode, userId, TokenId]);

    // ── Permission flags ───────────────────────────────────────────────────────
    // Actual API column names (from UserAuth/GetAllPermissionByModuleCodeUserId):
    //   Add, Edit, View, Delete, Search, Import, Export, MassUpdate, MassDelete
    //
    // Default to true while loading (avoids flickering hidden UI elements),
    // then strictly enforces once permissions object is available.
    const canAdd       = permissions ? permissions.Add        !== false : true;
    const canEdit      = permissions ? permissions.Edit       !== false : true;
    const canView      = permissions ? permissions.View       !== false : true;
    const canDelete    = permissions ? permissions.Delete     !== false : true;
    const canSearch    = permissions ? permissions.Search     !== false : true;
    const canExport    = permissions ? permissions.Export     !== false : true;
    const canImport    = permissions ? permissions.Import     !== false : true;
    const canMassUpdate = permissions ? permissions.MassUpdate !== false : true;
    const canMassDelete = permissions ? permissions.MassDelete !== false : true;


    return {
        permissions,
        loading,
        canAdd,
        canEdit,
        canView,
        canDelete,
        canSearch,
        canExport,
        canImport,
        canMassUpdate,
        canMassDelete,
        PermissionDeniedComponent
    };
};
// Standard Access Denied screen
export const PermissionDeniedComponent = () => {
    const navigate = useNavigate();
    return (
        <div className="cn-access-denied-container" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            backgroundColor: '#f6f8fa',
            padding: '24px',
            fontFamily: '"Inter", "Outfit", sans-serif',
            width: '100%'
        }}>
            <div style={{
                width: '100%',
                maxWidth: '560px',
                backgroundColor: '#ffffff',
                border: '1px dashed #d1d5db',
                borderRadius: '4px',
                padding: '40px 32px',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
                textAlign: 'center',
                margin: '20px auto'
            }}>
                {/* Brand Logo */}
                <div style={{ display: 'inline-block', backgroundColor: '#ffffff', padding: '4px 12px', border: '1px solid #e5e7eb', borderRadius: '4px', marginBottom: '28px' }}>
                    <span style={{ fontSize: '32px', fontWeight: '800', color: '#15803d', letterSpacing: '-0.5px' }}>Kit</span>
                    <span style={{ fontSize: '32px', fontWeight: '800', color: '#22c55e', letterSpacing: '-0.5px' }}>19</span>
                </div>

                {/* Lock laptop SVG */}
                <svg width="180" height="120" viewBox="0 0 220 150" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto 24px', display: 'block' }}>
                    <path d="M78 68V42C78 28.7 88.7 18 102 18H118C131.3 18 142 28.7 142 42V68" stroke="#a8a29e" strokeWidth="6" strokeLinecap="round" />
                    <rect x="36" y="58" width="148" height="74" rx="6" fill="#f8fafc" stroke="#a8a29e" strokeWidth="5" />
                    <rect x="80" y="68" width="60" height="48" rx="6" fill="#ffffff" stroke="#78716c" strokeWidth="5" />
                    <circle cx="110" cy="86" r="6" fill="#78716c" />
                    <path d="M110 92V104" stroke="#78716c" strokeWidth="4" strokeLinecap="round" />
                    <path d="M16 132C16 132 26 142 36 142H184C194 142 204 132 204 132" stroke="#a8a29e" strokeWidth="6" strokeLinecap="round" />
                    <line x1="86" y1="137" x2="134" y2="137" stroke="#a8a29e" strokeWidth="4" strokeLinecap="round" />
                </svg>

                {/* Heading */}
                <h2 style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#443e3c',
                    margin: '0 0 8px 0',
                    letterSpacing: '-0.5px'
                }}>
                    Access Denied/Forbidden
                </h2>

                {/* Subtitle */}
                <p style={{
                    fontSize: '14px',
                    color: '#78716c',
                    margin: '0 0 32px 0',
                    lineHeight: '1.6'
                }}>
                    The Page or resource you were trying to reach is not authorised to access
                </p>

                {/* Footer Actions */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: '1px solid #f3f4f6',
                    paddingTop: '20px'
                }}>
                    <span 
                        onClick={() => navigate('/')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: '#0284c7',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                        &lt; Back to Dashboard
                    </span>
                    
                    <span 
                        onClick={() => handleLogout(navigate)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: '#0284c7',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                        Logout
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px' }}>
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                    </span>
                </div>
            </div>
        </div>
    );
};
